import { useMemo } from 'react';

import { getFeatureFlag, isRootTenancy, useConfig, type FeatureFlagName } from '@/shared/config';
import { can, usePermissions, type PermissionResource } from '@/shared/permissions';
import {
  canAccessScreen,
  screenTrail,
  sectionOf,
  type Permission,
  type ScreenAccessEnvironment,
  type ScreenManifest,
} from '@/shared/screen';

import { screens } from '../screens';

import { LEGACY_NAV, sectionAccess } from './navigation';

/**
 * Um ambiente de acesso só para a guarda da rota, o menu, a paleta e o agente: folha (`permission`,
 * `access.anyOf`, tenant, flag), cada tela-mãe da trilha **e** seção, como o `RoutePermissionGuard`
 * do legado.
 */
export function useScreenAccess() {
  const { model, isPending } = usePermissions();
  const config = useConfig();
  const access = useMemo(() => {
    const rootTenancy = isRootTenancy(config);
    const flag = (key: string): unknown =>
      key.startsWith('FEATURE_FLAGS.')
        ? getFeatureFlag(config, key.slice(14) as FeatureFlagName)
        : (config as Record<string, unknown>)[key];
    const canPermission = (p: Permission) =>
      can(
        model,
        p.resource as PermissionResource,
        p.action,
        p.strict === undefined ? undefined : { strict: p.strict },
      );
    const section = sectionAccess(LEGACY_NAV, {
      can: canPermission,
      isRootTenancy: rootTenancy,
      flag,
    });
    const env: ScreenAccessEnvironment = {
      can: (resource, action, options) => can(model, resource as PermissionResource, action, options),
      rootTenancy,
      flag,
      section: (screen) => {
        const id = sectionOf(screens, screen);
        return id === undefined || section(id);
      },
      trail: (screen) => screenTrail(screens, screen),
    };
    return {
      rootTenancy,
      flag,
      canPermission,
      screenAllowed: (screen: ScreenManifest) => canAccessScreen(screen, env),
      env,
    };
  }, [model, config]);
  return { ...access, isPending };
}
